// src/features/streams/components/simulcast/FacebookDestinationFlow.tsx

import { useState, useMemo } from 'react';
import {
  Stack,
  Button,
  Text,
  Alert,
  Select,
  TextInput,
  Group,
  Loader,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck } from '@tabler/icons-react';
import { FacebookLogo } from './PlatformLogos';
import { useForm, useController } from 'react-hook-form';
import { BroadcastMetadataOverrideSection } from './BroadcastMetadataOverrideSection';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { MEDIA_QUERY_KEYS } from '@/lib/constants/media';
import { EXTPLAT_QUERY_KEYS } from '@/lib/constants/extplat';
import { SIMULCAST_CONSTRAINTS } from '@/lib/constants/simulcast';
import {
  type ISimulcastAllData,
  type ISimulcastFBCreateReq,
  type IFBLocations,
  FBLocationType,
  FacebookUtil,
  ExtPlatAuthUtil,
} from '@/lib/types/Simulcast';
import type { StreamViewData } from '@/lib/types/Stream';
import { fbCreateFormSchema, type FBCreateFormData } from '../../schemas/SimulcastForm.schema';
import { SimulcastTransforms } from '../../simulcast/simulcastTransforms';
import { useFacebookSdk } from '../../hooks/useFacebookSdk';
import { useExtPlatAuths } from '../../useExtPlatAuth';
import { useCreateSimulcastFB } from '../../useSimulcastMutations';
import { ExtPlatConfig } from '@/shared/utils/extplatBrandConfig';
import { ExtPlatApi } from '@/shared/api/ExtPlatApi';

// --- Types ---

type FlowStage =
  | { stage: 'connect' }
  | { stage: 'processing'; message: string }
  | { stage: 'configure'; extPlatAuthId: number; locations: IFBLocations; userName: string }
  | { stage: 'error'; message: string };

interface FacebookDestinationFlowProps {
  mode: 'create' | 'reconnect';
  stream: StreamViewData;
  simulcast?: ISimulcastAllData;
  onClose: () => void;
  onBack: () => void;
}

// --- Helpers ---

function getFBMe(): Promise<{ id: string; name: string }> {
  return new Promise((resolve, reject) => {
    window.FB.api('/me', (response: { id?: string; name?: string; error?: unknown }) => {
      if (response.error || !response.id || !response.name) {
        reject(new Error('Failed to get Facebook user info'));
      } else {
        resolve({ id: response.id, name: response.name });
      }
    });
  });
}

interface LocationSelection {
  type: FBLocationType;
  id: string;
  label: string;
  pageAccessToken: string | null;
}

function parseLocationValue(value: string): { type: FBLocationType; id: string } {
  const colonIdx = value.indexOf(':');
  return {
    type: value.substring(0, colonIdx) as FBLocationType,
    id: value.substring(colonIdx + 1),
  };
}

function resolveLocation(value: string, locations: IFBLocations): LocationSelection {
  const { type, id } = parseLocationValue(value);
  let label = '';
  let pageAccessToken: string | null = null;

  switch (type) {
    case FBLocationType.USER:
      label = locations.name;
      break;
    case FBLocationType.PAGE: {
      const page = locations.pages.find(p => p.id === id);
      label = page?.name ?? '';
      pageAccessToken = page?.access_token ?? null;
      break;
    }
    case FBLocationType.GROUP: {
      const group = locations.groups.find(g => g.id === id);
      label = group?.name ?? '';
      break;
    }
    case FBLocationType.EVENT: {
      const event = locations.events.find(e => e.id === id);
      label = event?.name ?? '';
      break;
    }
  }

  return { type, id, label, pageAccessToken };
}

type ComboboxItemGroup = { group: string; items: { value: string; label: string }[] };

function buildLocationOptions(locations: IFBLocations): ComboboxItemGroup[] {
  const groups: ComboboxItemGroup[] = [];

  if (locations.id && locations.name) {
    groups.push({
      group: 'Profile',
      items: [{ value: `${FBLocationType.USER}:${locations.id}`, label: locations.name }],
    });
  }

  if (locations.pages.length > 0) {
    groups.push({
      group: 'Pages',
      items: locations.pages.map(p => ({
        value: `${FBLocationType.PAGE}:${p.id}`,
        label: p.name,
      })),
    });
  }

  if (locations.groups.length > 0) {
    groups.push({
      group: 'Groups',
      items: locations.groups.map(g => ({
        value: `${FBLocationType.GROUP}:${g.id}`,
        label: g.name,
      })),
    });
  }

  if (locations.events.length > 0) {
    groups.push({
      group: 'Events',
      items: locations.events.map(e => ({
        value: `${FBLocationType.EVENT}:${e.id}`,
        label: e.name,
      })),
    });
  }

  return groups;
}

// --- Configure Form (create stage 2) ---

interface ConfigureFormProps {
  extPlatAuthId: number;
  locations: IFBLocations;
  userName: string;
  stream: StreamViewData;
  onSubmit: (req: ISimulcastFBCreateReq) => void;
  onBack: () => void;
  isPending: boolean;
}

function ConfigureForm({
  extPlatAuthId,
  locations,
  userName,
  stream,
  onSubmit,
  onBack,
  isPending,
}: ConfigureFormProps) {
  const defaultLocation = useMemo(() => FacebookUtil.getDefaultLocation(locations), [locations]);
  const defaultLocationValue = `${defaultLocation.type}:${defaultLocation.id}`;
  const locationOptions = useMemo(() => buildLocationOptions(locations), [locations]);

  const [selectedLocation, setSelectedLocation] = useState(defaultLocationValue);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FBCreateFormData>({
    resolver: zodResolver(fbCreateFormSchema),
    defaultValues: {
      label: defaultLocation.name,
      title: '',
      descShort: '',
    },
  });

  const { field: labelField } = useController({ name: 'label', control });

  const handleLocationChange = (value: string | null) => {
    if (!value) return;
    setSelectedLocation(value);
    const loc = resolveLocation(value, locations);
    setValue('label', loc.label);
  };

  const onFormSubmit = handleSubmit((data) => {
    const loc = resolveLocation(selectedLocation, locations);
    const req = SimulcastTransforms.buildFBCreateReq(
      extPlatAuthId,
      loc.id,
      loc.label,
      loc.type,
      loc.pageAccessToken,
      data,
    );
    onSubmit(req);
  });

  return (
    <form onSubmit={onFormSubmit}>
      <Stack gap="md">
        <Alert color="green" icon={<IconCheck size={16} />}>
          Connected as &ldquo;{userName}&rdquo;
        </Alert>

        <Select
          label="Stream to"
          data={locationOptions}
          value={selectedLocation}
          onChange={handleLocationChange}
          allowDeselect={false}
        />

        <TextInput
          label="Destination Name"
          description="A name to identify this destination in your dashboard"
          placeholder="e.g., Production Facebook Page"
          value={labelField.value}
          onChange={labelField.onChange}
          onBlur={labelField.onBlur}
          error={errors.label?.message}
          maxLength={SIMULCAST_CONSTRAINTS.LABEL_MAX}
        />

        <BroadcastMetadataOverrideSection
          streamTitle={stream.media.title}
          streamDescription={stream.media.descShort ?? ''}
          control={control}
          errors={errors}
        />

        <Group justify="space-between" mt="md">
          <Button variant="subtle" onClick={onBack} disabled={isPending}>
            Back
          </Button>
          <Button type="submit" loading={isPending}>
            Create
          </Button>
        </Group>
      </Stack>
    </form>
  );
}

// --- Main Component ---

export function FacebookDestinationFlow({
  mode,
  stream,
  simulcast,
  onClose,
  onBack,
}: FacebookDestinationFlowProps) {
  const { selectedCId, selectedCustomer } = useAuth();
  const queryClient = useQueryClient();
  const fbAppId = ExtPlatConfig.getFacebookAppId(selectedCustomer?.cfg.reseller);
  const sdk = useFacebookSdk(fbAppId);
  const { data: extPlatAuths } = useExtPlatAuths();
  const createSimulcastFB = useCreateSimulcastFB(stream.media.mediaId);

  const [flowStage, setFlowStage] = useState<FlowStage>({ stage: 'connect' });

  const handleConnect = async () => {
    setFlowStage({ stage: 'processing', message: 'Connecting to Facebook...' });
    try {
      const loginResult = await sdk.login();
      const me = await getFBMe();

      // Build ExtPlatAuth request
      const authReq = ExtPlatAuthUtil.createFBCreateOrUpdateReq(
        fbAppId,
        loginResult.accessToken,
        loginResult.expiresIn,
        me.name,
        loginResult.userID,
      );

      if (mode === 'reconnect') {
        // Reconnect: update existing ExtPlatAuth, then invalidate queries
        const extPlatAuthId = simulcast!.core.extPlatAuthId!;
        await ExtPlatApi.updateExtPlatAuth(selectedCId!, extPlatAuthId, authReq);

        queryClient.invalidateQueries({
          queryKey: MEDIA_QUERY_KEYS.stream(selectedCId!, stream.media.mediaId),
        });
        queryClient.invalidateQueries({
          queryKey: EXTPLAT_QUERY_KEYS.extPlatAuths(selectedCId!),
        });

        notifications.show({
          title: 'Reconnected',
          message: 'Facebook account has been reconnected.',
          color: 'green',
        });
        onClose();
        return;
      }

      // Create flow: dedup check for existing ExtPlatAuth with matching fbUserId
      const existingAuth = extPlatAuths?.find(a => a.fb.fbUserId === loginResult.userID);
      let authId: number;

      if (existingAuth) {
        const updated = await ExtPlatApi.updateExtPlatAuth(
          selectedCId!,
          existingAuth.extPlatAuthId,
          authReq,
        );
        authId = updated.extPlatAuthId;
      } else {
        const created = await ExtPlatApi.createExtPlatAuth(selectedCId!, authReq);
        authId = created.extPlatAuthId;
      }

      queryClient.invalidateQueries({
        queryKey: EXTPLAT_QUERY_KEYS.extPlatAuths(selectedCId!),
      });

      // Fetch and sort locations
      setFlowStage({ stage: 'processing', message: 'Loading Facebook locations...' });
      const locations = await ExtPlatApi.getFacebookLocations(selectedCId!, authId);
      FacebookUtil.sortLocations(locations);

      // Validate locations have at least one option
      const hasLocations =
        (locations.id && locations.name) ||
        locations.pages.length > 0 ||
        locations.groups.length > 0 ||
        locations.events.length > 0;

      if (!hasLocations) {
        setFlowStage({
          stage: 'error',
          message:
            'No Facebook destinations available. You need at least a page, group, or event to stream to.',
        });
        return;
      }

      setFlowStage({
        stage: 'configure',
        extPlatAuthId: authId,
        locations,
        userName: me.name,
      });
    } catch (err) {
      setFlowStage({
        stage: 'error',
        message: err instanceof Error ? err.message : 'Failed to connect to Facebook',
      });
    }
  };

  const handleCreateSubmit = (req: ISimulcastFBCreateReq) => {
    createSimulcastFB.mutate(req, {
      onSuccess: () => {
        notifications.show({
          title: 'Destination Created',
          message: `"${req.label}" has been added.`,
          color: 'green',
        });
        onClose();
      },
      onError: (err) => {
        notifications.show({
          title: 'Error',
          message: err.message,
          color: 'red',
        });
      },
    });
  };

  // Render configure form when in that stage
  if (flowStage.stage === 'configure') {
    return (
      <ConfigureForm
        extPlatAuthId={flowStage.extPlatAuthId}
        locations={flowStage.locations}
        userName={flowStage.userName}
        stream={stream}
        onSubmit={handleCreateSubmit}
        onBack={onBack}
        isPending={createSimulcastFB.isPending}
      />
    );
  }

  // Connect / processing / error stages
  return (
    <Stack align="center" gap="md" py="xl">
      <FacebookLogo size={64} />

      {flowStage.stage === 'connect' && (
        <>
          <Text ta="center" maw={350}>
            {mode === 'create'
              ? 'Connect your Facebook account to stream to your page, group, or profile.'
              : 'Reconnect your Facebook account to refresh your authentication.'}
          </Text>
          <Button size="md" color="blue" onClick={handleConnect}>
            {mode === 'create' ? 'Connect with Facebook' : 'Reconnect with Facebook'}
          </Button>
        </>
      )}

      {flowStage.stage === 'processing' && (
        <>
          <Loader size="md" color="blue" />
          <Text c="dimmed" size="sm">
            {flowStage.message}
          </Text>
        </>
      )}

      {flowStage.stage === 'error' && (
        <>
          <Alert color="red" w="100%">
            {flowStage.message}
          </Alert>
          <Button variant="light" onClick={() => setFlowStage({ stage: 'connect' })}>
            Try Again
          </Button>
        </>
      )}

      <Button variant="subtle" onClick={onBack} mt="md">
        Back
      </Button>
    </Stack>
  );
}
